import React from 'react';
import { Helmet } from 'react-helmet';
import '@fontsource/archivo/400.css';
import '@fontsource/archivo/500.css';
import '@fontsource/archivo/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
// Same scope as the intake form and the receipt page, so a client who backs out of checkout stays
// inside the same document rather than landing somewhere that looks like a different firm.
import '../IntakeView/IntakeView.scss';

/**
 * Where Stripe sends a client who abandons checkout (`cancel_url` in PublicIntakeRouter).
 *
 * This path did not exist. `cancel_url` has always pointed at `${INTAKE_URL}/cancelled`, and the
 * router's `path: "*"` answered it with NotFoundView, so a client who thought better of paying was
 * told the page did not exist. Same fault as `/thank-you` and `/received` before Aug 14 2026.
 *
 * Not folded into IntakeReturnView: that page is a receipt, built around a payment that succeeded,
 * and its own header says its two outcomes differ by two paragraphs and must never drift apart.
 * Nothing was charged here, so the stamp, the fee panel and the "what happens next" sequence are all
 * wrong, and conditionalising them would leave a receipt that sometimes denies being one.
 *
 * No link back to the form. Stripe returns nothing, so the product and tier they were part way
 * through are unknown, and `/` on this subdomain is the portal login rather than intake. A wrong
 * link is worse than the phone number, which reaches someone who can see the held session.
 *
 * Carries no personal data, for the same reason the receipt page does not: the analytics commitment
 * made about this subdomain.
 */

const OFFICE_PHONE = '210-210-1616';

const IntakeCancelledView = () => (
  <div className="intake">
    <Helmet>
      <title>Payment not completed</title>
    </Helmet>

    <header className="intake__bar">
      <span className="intake__mark">Texas Ticket Doctor</span>
      <a className="intake__phone" href={`tel:${OFFICE_PHONE}`}>
        {OFFICE_PHONE}
      </a>
    </header>

    <main className="intake__sheet">
      <div className="intake__masthead">
        <p className="intake__eyebrow">Payment</p>
        <h1 className="intake__title">You have not been charged</h1>
      </div>

      {/* The one thing a client can get wrong from here is believing they paid, so it is said first
          and plainly. */}
      <section className="intake__fee">
        <p className="intake__field-label">Payment</p>
        <p className="intake__amount-note">
          You left the payment page before it finished, so nothing was taken from your card and no
          case has been opened.
        </p>
      </section>

      <p className="intake__legal">
        If you still want us on this ticket, go back in your browser to finish, or call{' '}
        <a href={`tel:${OFFICE_PHONE}`}>{OFFICE_PHONE}</a> and say you were part way through online.
        {/* Not "for an hour": the session's hour runs from when the form was started, not from
            when checkout was abandoned, so a client who took forty minutes has twenty left. */}{' '}
        Your answers are held for a short time.
      </p>

      <p className="intake__legal">
        Your court date does not move while you decide. If the date is close, call us rather than
        wait.
      </p>
    </main>
  </div>
);

export default IntakeCancelledView;
